import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IoIosCloseCircle } from "react-icons/io";
import {
  useCreateSubAdminMutation,
  useUpdateSubAdminMutation,
  useGetSubAdminByIdQuery,
  useGetRolesQuery,
} from "Slices/rollManager/subAdmin.slice";
import { cmsSegments } from "constants/cmsSegments";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: ${({ show }) => (show ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  z-index: 1200;
  padding: 24px;
`;

const Modal = styled.div`
  background: #ffffff;
  width: min(960px, 95vw);
  border-radius: 16px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.25);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #eceff3;
`;

const Title = styled.h3`
  margin: 0;
  font-weight: 700;
  letter-spacing: 0.2px;
`;

const CloseIcon = styled(IoIosCloseCircle)`
  font-size: 28px;
  cursor: pointer;
  color: #94a3b8;
  transition: color 0.2s ease;
  &:hover {
    color: #0f172a;
  }
`;

const Body = styled.div`
  padding: 20px 24px 26px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: #0f172a;
`;

const Input = styled.input`
  height: 42px;
  border: 1px solid #d8dde4;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
  transition: border 0.2s ease, box-shadow 0.2s ease;
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
`;

const Select = styled.select`
  height: 42px;
  border: 1px solid #d8dde4;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
  background: #f8fafc;
  transition: border 0.2s ease, box-shadow 0.2s ease;
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }
`;

const SegmentsWrap = styled.div`
  border: 1px solid #d8dde4;
  border-radius: 12px;
  padding: 12px;
  background: #f8fafc;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #0f172a;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button`
  min-width: 120px;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
  border: 1px solid ${({ variant }) => (variant === "ghost" ? "#e2e8f0" : "#2563eb")};
  background: ${({ variant }) => (variant === "ghost" ? "#ffffff" : "#2563eb")};
  color: ${({ variant }) => (variant === "ghost" ? "#0f172a" : "#ffffff")};
  cursor: pointer;
  box-shadow: ${({ variant }) =>
    variant === "ghost" ? "none" : "0 10px 30px rgba(37, 99, 235, 0.25)"};
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ variant }) =>
      variant === "ghost"
        ? "0 10px 30px rgba(15, 23, 42, 0.06)"
        : "0 12px 34px rgba(37, 99, 235, 0.3)"};
  }
`;

const Helper = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 13px;
`;

const CreateSubAdmin = ({ showForm, setShowForm, editMode, subAdminId }) => {
  const [createSubAdmin] = useCreateSubAdminMutation();
  const [updateSubAdmin] = useUpdateSubAdminMutation();
  const { data: subAdminData } = useGetSubAdminByIdQuery(subAdminId, { skip: !editMode });
  const { data: roles = [], isLoading: rolesLoading } = useGetRolesQuery();

  const availableSegments = cmsSegments
    .filter((seg) => seg.assignable !== false)
    .map((seg) => seg.title);

  const [formData, setFormData] = useState({
    name: "",
    contactNumber: "",
    location: "",
    roleId: "",
    id: "",
    password: "",
    assignedSections: [],
  });

  // Load existing data on edit
  useEffect(() => {
    if (editMode && subAdminData) {
      setFormData({
        name: subAdminData.name || "",
        contactNumber: subAdminData.contactNumber || "",
        location: subAdminData.location || subAdminData.district || "",
        roleId: subAdminData.role?._id || "",
        id: subAdminData.id || "",
        password: "",
        assignedSections: subAdminData.assignedSections || [],
      });
    }
  }, [editMode, subAdminData]);

  // Default role selection when creating
  useEffect(() => {
    if (!editMode && roles.length > 0 && !formData.roleId) {
      setFormData((prev) => ({ ...prev, roleId: roles[0]._id }));
    }
  }, [roles, editMode, formData.roleId]);

  const toggleSegment = (segment) => {
    setFormData((prev) => {
      const isSelected = prev.assignedSections.includes(segment);
      return {
        ...prev,
        assignedSections: isSelected
          ? prev.assignedSections.filter((s) => s !== segment)
          : [...prev.assignedSections, segment],
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await updateSubAdmin({
          id: subAdminId,
          ...formData,
          district: formData.location,
          location: formData.location,
          roleId: formData.roleId,
        }).unwrap();
        alert("✅ Sub-Admin updated successfully!");
      } else {
        await createSubAdmin({
          ...formData,
          district: formData.location,
          location: formData.location,
          roleId: formData.roleId,
        }).unwrap();
        alert("✅ Sub-Admin created successfully!");
      }
      setShowForm(false);
    } catch (error) {
      alert("❌ Failed to save Sub-Admin. Try again.");
      console.error("❌ Error:", error);
    }
  };

  return (
    <Overlay show={showForm}>
      <Modal>
        <Header>
          <div>
            <Title>{editMode ? "Edit Sub-Admin" : "Create Sub-Admin"}</Title>
            <Helper>Assign a role and content segments to control access.</Helper>
          </div>
          <CloseIcon onClick={() => setShowForm(false)} />
        </Header>

        <Body>
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter full name"
                />
              </Field>

              <Field>
                <Label>Contact Number</Label>
                <Input
                  type="text"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  placeholder="e.g. +91 9876543210"
                />
              </Field>

              <Field>
                <Label>Location</Label>
                <Input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Enter location"
                />
              </Field>

              <Field>
                <Label>Role</Label>
                <Select
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleChange}
                  disabled={rolesLoading}
                  required
                >
                  <option value="" disabled>
                    {rolesLoading ? "Loading roles..." : "Select role"}
                  </option>
                  {roles.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.name}
                    </option>
                  ))}
                </Select>
                {!rolesLoading && roles.length === 0 && (
                  <Helper>No roles found. Please create a role first.</Helper>
                )}
              </Field>

              <Field>
                <Label>Login ID</Label>
                <Input
                  type="text"
                  name="id"
                  value={formData.id}
                  onChange={handleChange}
                  required
                  placeholder="e.g. user@domain.com"
                />
              </Field>

              {!editMode && (
                <Field>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Choose a strong password"
                  />
                </Field>
              )}
            </FormGrid>

            <Field>
              <Label>Assign Segments</Label>
              <SegmentsWrap>
                <CheckboxGrid>
                  {availableSegments.map((segment) => (
                    <CheckboxLabel key={segment}>
                      <input
                        type="checkbox"
                        checked={formData.assignedSections.includes(segment)}
                        onChange={() => toggleSegment(segment)}
                      />
                      {segment}
                    </CheckboxLabel>
                  ))}
                </CheckboxGrid>
              </SegmentsWrap>
              <Helper>Select which content areas this sub-admin can manage.</Helper>
            </Field>

            <Footer>
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button type="submit">{editMode ? "Update Sub-Admin" : "Create Sub-Admin"}</Button>
            </Footer>
          </form>
        </Body>
      </Modal>
    </Overlay>
  );
};

export default CreateSubAdmin;
